import React from 'react';
import { Label, Pagination, Modal, Icon } from 'semantic-ui-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Table from 'semantic-table-grid';
import _ from 'lodash';

// Components
import { BtnWithActions } from 'components/Buttons';
import ImpresaFilterBar from '../ImpresaFilterBar';
import AnnullaProcessoImpresa from '../AnnullaPorcessoImpresa';
import currencyFormatter from '../../../shared/lib/currencyFormatter';

// Constants
import { ROUTES } from '../../../shared/constants';

// Services
import ImpresaService from '../../../shared/services/api/impresa';

const TableOverflow = Styled.div`
  display: block;
  margin: -14px -14px -10px;
  overflow-x: auto;
  padding: 14px;
  & td {
    position: relative;
  }
`;

const PaginationWrapper = Styled.div`
  .item[type='pageItem'],
  .item[tabIndex='-1'] {
    display: none !important;
  }
`;

const ImportoBadgeStyle = {
  position: 'absolute',
  whiteSpace: 'nowrap',
  fontSize: 9,
  top: -12,
  right: '-10%',
};

export class ImpresaList extends React.Component {
  constructor(props) {
    super(props);
    this.tableContainerRef = null;

    this.state = {
      // Neo profiles loading
      isLoading: false,
      // Manage pagination of table
      page: 1,
      totalPages: 1,
      pageSize: 25,
      // Manage filter
      lastFilters: {},
      // All candidates to show
      impreseList: [],
      // Manage order params for table
      orderField: 'updatedAt',
      orderDirection: 'desc',
      modalAnnullaOpened: false,
      impresaToDelete: null,
    };
  }

  componentDidMount() {
    this.onSearch(this.state.lastFilters);
    this.fixActionHeaderColSpan();
  }

  componentDidUpdate() {
    this.fixActionHeaderColSpan();
  }

  /** Redirect to new impresa page */
  onClickNewImpresa = () => this.props.history.push(`${ROUTES.PROCESSO_IMPRESE}/new`);
  onClose = () => {
    this.setState({
      modalAnnullaOpened: false,
      impresaToDelete: null,
    });
  };

  onSearch = (dataSearch) => {
    const filters = dataSearch || this.state.lastFilters;

    this.setState({
      isLoading: true,
      lastFilters: filters,
    });

    ImpresaService.getAll(filters, this.state.page, this.state.orderField, this.state.orderDirection)
      .then((res) => {
        this.setState({
          page: res.page || this.state.page,
          pageSize: res.page_size,
          totalPages: res.page_count,
          impreseList: _.get(res, '_embedded.processo_imprese', []),
        });
      })
      .catch(() => {
        this.props.notification().addNotification({
          message: 'Errore nel recupero della lista delle imprese',
          level: 'error',
        });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  onReloadList = () => {
    this.onSearch(this.state.lastFilters);
  };

  getImportoLabel(data) {
    const floating = {
      float: 'right',
      margin: 0,
      position: 'relative',
    };

    if (data.importoErogato || data.importoRichiesto) {
      let importo = 0;
      let label = '';

      if (data.importoErogato) {
        importo = data.importoErogato;
        label = data.tipoImporto || 'Erogato';
      } else {
        importo = data.importoRichiesto;
        label = data.tipoImporto || 'Richiesto';
      }

      return (
        <Label basic style={floating}>
          {currencyFormatter.format(importo)}
          <Label circular style={ImportoBadgeStyle}>
            {label}
          </Label>
        </Label>
      );
    }

    return (
      <Label basic style={floating}>
        {currencyFormatter.format(0)}
      </Label>
    );
  }

  getStatoLabel(stato) {
    if (stato === 'chiusa') {
      return 'conclusa';
    }

    if (stato === 'chiusa_negativa') {
      return 'chiusa negativa';
    }

    return stato;
  }

  getOtherActions = (impresa) => {
    if (impresa.stato === 'aperta') {
      return [
        {
          icon: 'remove',
          label: 'Annulla processo impresa',
          action: () => this.setState({ modalAnnullaOpened: true, impresaToDelete: impresa.id }),
        },
      ];
    }

    return [];
  };

  /** Define action for each impresa */
  getActions = (impresa) =>
    [
      {
        icon: 'edit',
        label: 'Modifica',
        action: () => this.props.history.push(`${ROUTES.PROCESSO_IMPRESE}/${impresa.id}`),
      },
    ].filter(Boolean);

  /** Check direction of menu */
  isMenuUpward = (impresa) => this.state.impreseList.length - _.findLastIndex(this.state.impreseList, impresa) < 3;

  fixActionHeaderColSpan = () => {
    const tableContainer = this.tableContainerRef;

    if (!tableContainer) {
      return;
    }

    // Workaround for semantic-table-grid header toolbar colSpan hardcoded to 12.
    const actionHeaderCell = tableContainer.querySelector('th[colspan="12"]');

    if (actionHeaderCell) {
      actionHeaderCell.setAttribute('colspan', '18');
      actionHeaderCell.style.userSelect = 'none';
    }
  };

  columns = [
    {
      key: 'codice',
      name: 'N°',
      formatter: ({ data }) => (
        <div>
          <a
            style={{ display: 'inline-block', textAlign: 'center', position: 'relative', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => this.props.history.push(`${ROUTES.PROCESSO_IMPRESE}/${data.id}`)}
          >
            {data.codice}
          </a>
        </div>
      ),
    },
    {
      key: 'nominativoIntestatario',
      name: 'Nominativo',
      formatter: ({ data }) => <span style={{ textTransform: 'uppercase' }}>{data.intestatario.nominativo}</span>,
    },
    {
      key: 'partitaiva',
      name: 'Partita IVA',
      formatter: ({ data }) => <span>{data.intestatario.partitaiva}</span>,
    },
    {
      key: 'istituto',
      name: 'Istituto',
      formatter: ({ data }) => <span>{data.istituto || '--'}</span>,
    },
    {
      key: 'tipoGestione',
      name: 'Tipo gestione',
      formatter: ({ data }) => <span>{data.tipoGestione || '--'}</span>,
    },
    {
      key: 'tipoProdotto',
      name: 'Tipo operazione',
      formatter: ({ data }) => <span>{data.tipoProdotto || '--'}</span>,
    },
    {
      key: 'createdAt',
      name: 'Data creazione',
      formatter: ({ data }) => (
        <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          {moment.unix(data.createdAt).format('DD/MM/YYYY')}
        </span>
      ),
      sortable: true,
      width: '140px',
    },
    {
      key: 'dataErograzione',
      name: 'Data erogazione',
      formatter: ({ data }) =>
        data.dataErogazione ? (
          <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            {moment.unix(data.dataErogazione).format('DD/MM/YYYY')}
          </span>
        ) : null,
      sortable: true,
      width: '140px',
    },
    {
      key: 'stato',
      name: 'Stato',
      formatter: ({ data }) => (
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <Label
            // eslint-disable-next-line no-nested-ternary
            color={data.stato === 'aperta' ? 'blue' : data.stato === 'chiusa' ? 'green' : 'red'}
            style={{ margin: 0, position: 'relative', textTransform: 'uppercase' }}
          >
            {this.getStatoLabel(data.stato)}
          </Label>
        </div>
      ),
      width: '1%',
    },
    {
      key: 'contrattoMediazione',
      name: 'Contratto mediazione',
      formatter: ({ data }) =>
        data.contrattoMediazione !== null &&
        data.contrattoMediazione !== '' &&
        data.contrattoMediazione !== '00000000-0000-0000-0000-000000000000' ? (
          <div style={{ textAlign: 'center' }}>
            <Icon name="check" color="green" />
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Icon name="x" color="red" />
          </div>
        ),
      sortable: false,
      width: '140px',
    },
    {
      key: 'importo',
      name: 'Importo',
      formatter: ({ data }) => this.getImportoLabel(data),
      width: '1%',
    },
    {
      key: 'codiceAgenziaKiron',
      name: 'Agenzia',
      width: '1%',
    },
    {
      key: 'nominativoSenior',
      name: 'Senior',
      formatter: ({ data }) => <span style={{ whiteSpace: 'nowrap' }}>{data.nominativoSenior}</span>,
    },
    {
      key: 'nominativoCollaboratore',
      name: 'Collaboratore',
      formatter: ({ data }) => <span style={{ whiteSpace: 'nowrap' }}>{data.nominativoCollaboratore}</span>,
      sortable: true,
      width: 'auto',
    },
    {
      key: 'Azioni',
      name: 'Azioni',
      textAlign: 'center',
      formatter: ({ data }) => (
        <div style={{ textAlign: 'center' }}>
          <BtnWithActions
            key="other-actions"
            actions={this.getActions(data)}
            otherActions={this.getOtherActions(data)}
            upward={this.isMenuUpward(data)}
          />
        </div>
      ),
      width: 'auto',
    },
  ];

  loadData = (filters) => {
    this.setState({ page: 1 }, () => this.onSearch(filters));
  };

  render() {
    const { isLoading, impreseList, totalPages, page } = this.state;
    return (
      <TableOverflow
        innerRef={(node) => {
          this.tableContainerRef = node;
        }}
      >
        <Table
          isLoading={isLoading}
          columns={this.columns}
          elements={impreseList}
          canAction={{
            actions: <ImpresaFilterBar onClickNewImpresa={this.onClickNewImpresa} onFilter={this.loadData} />,
            active: true,
          }}
          canPaginate={{
            active: true,
            render: (
              <PaginationWrapper>
                <Pagination
                  activePage={page || 1}
                  boundaryRange={1}
                  onPageChange={(_e, { activePage }) =>
                    this.setState({ page: activePage }, () => this.onSearch(this.state.lastFilters))
                  }
                  size="mini"
                  siblingRange={1}
                  totalPages={totalPages || 99999}
                  ellipsisItem={'...'}
                  lastItem={null}
                />
              </PaginationWrapper>
            ),
          }}
        />

        <Modal
          open={this.state.modalAnnullaOpened}
          onClose={this.onClose}
          title={
            <div>
              <h2>
                <Icon name="id card" />
                Annulla processo impresa
              </h2>
            </div>
          }
          size={'mini'}
          style={{ width: '200px!important', padding: '20px' }}
        >
          <AnnullaProcessoImpresa
            impresaId={this.state.impresaToDelete}
            close={this.onClose}
            update={this.loadData}
            notifications={this.props.notification}
          />
        </Modal>
      </TableOverflow>
    );
  }
}

ImpresaList.propTypes = {
  /** Used for manage history of the browser -> redirect */
  history: PropTypes.object,
  /** Containes information about the URL -> used for get the neo profile ID  */
  // match: PropTypes.object,
  /** Function for call the notification system */
  notification: PropTypes.func,
};

export default ImpresaList;



// WEBPACK FOOTER //
// ./app/components/ProcessoImpresa/ImpresaList/index.js